import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { ChipV2, Drawer, SearchInput } from "components";
import { InfoIcon } from "components/icons";
import { useGetCustomerMembers } from "controller/hooks/customer/useCustomerMembers";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { CustomerMemberItem } from "shared";
import { MemberListItem } from "shared/features/chat/components/ChatMembersDrawer/MemberListItem";
import { getFullName } from "utilz/helpers";
import * as yup from "yup";

export interface CcMember {
  user_id: string;
  member_type: "resource" | "customer" | "external";
  display_name: string;
  first_name?: string;
  last_name?: string;
  email: string;
}

interface SelectParticipantsDrawerProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  selectedMembers: CcMember[];
  onAdd: (members: CcMember[]) => void;
}

const emailSchema = yup.string().email();

export const SelectParticipantsDrawer = memo(
  ({
    open,
    onClose,
    customerId,
    selectedMembers,
    onAdd,
  }: SelectParticipantsDrawerProps) => {
    const theme = useTheme();
    const { orange } = theme.palette.primitive.system;

    // Local state
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [externalEmails, setExternalEmails] = useState<string[]>([]);

    // Initialize state when drawer opens
    useEffect(() => {
      if (open) {
        const ids = new Set(
          selectedMembers
            .filter((m) => m.member_type !== "external")
            .map((m) => m.user_id),
        );
        setSelectedIds(ids);
        setExternalEmails(
          selectedMembers
            .filter((m) => m.member_type === "external")
            .map((m) => m.email),
        );
        setSearchQuery("");
        setDebouncedSearch("");
      }
    }, [open, selectedMembers]);

    // Debounced search for API
    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
      return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch customer members
    const { data: customerMembersData, isFetching } = useGetCustomerMembers(
      customerId,
      debouncedSearch || undefined,
      open && Boolean(customerId),
    );

    // Flatten resources + users into unified list
    const allMembers = useMemo((): CustomerMemberItem[] => {
      if (!customerMembersData) return [];

      const resources: CustomerMemberItem[] =
        customerMembersData.resources.result.map((r) => ({
          mapping_id: r.mapping_id,
          email: r.email,
          first_name: r.first_name,
          last_name: r.last_name,
          display_name: r.display_name,
          is_active: r.is_active,
          is_locked: r.is_locked,
          member_type: "resource" as const,
          limited_access: r.limited_access,
        }));

      const users: CustomerMemberItem[] = customerMembersData.users.result.map(
        (u) => ({
          mapping_id: u.mapping_id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          display_name: u.display_name,
          is_active: u.is_active,
          is_locked: u.is_locked,
          member_type: "customer" as const,
        }),
      );

      return [...resources, ...users];
    }, [customerMembersData]);

    // Client-side filter on display_name/email
    const filteredMembers = useMemo(() => {
      const query = searchQuery?.toLowerCase()?.trim();
      if (!query) return allMembers;
      return allMembers.filter(
        (m) =>
          m.first_name?.toLowerCase().includes(query) ||
          m.last_name?.toLowerCase().includes(query) ||
          m.email?.toLowerCase().includes(query),
      );
    }, [allMembers, searchQuery]);

    // Total count for display
    const totalCount = allMembers.length;

    // Email validation
    const trimmedQuery = searchQuery.trim();
    const isValidEmail = useMemo(() => {
      if (!trimmedQuery) return false;
      return emailSchema.isValidSync(trimmedQuery);
    }, [trimmedQuery]);

    const isEmailAlreadyAdded = useMemo(() => {
      if (!isValidEmail) return false;
      const lower = trimmedQuery.toLowerCase();
      if (externalEmails.includes(lower)) return true;
      return allMembers.some((m) => m.email?.toLowerCase() === lower);
    }, [isValidEmail, trimmedQuery, externalEmails, allMembers]);

    const showEmailDropdown =
      isValidEmail &&
      !isEmailAlreadyAdded &&
      filteredMembers.length === 0 &&
      !isFetching;

    // Toggle member selection
    const handleToggle = useCallback((userId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) {
          next.delete(userId);
        } else {
          next.add(userId);
        }
        return next;
      });
    }, []);

    // Search change
    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    // Add external email
    const handleAddExternalEmail = useCallback(() => {
      if (!showEmailDropdown) return;
      setExternalEmails((prev) => [...prev, trimmedQuery.toLowerCase()]);
      setSearchQuery("");
    }, [showEmailDropdown, trimmedQuery]);

    // Handle Enter key to add external email
    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && showEmailDropdown) {
          e.preventDefault();
          handleAddExternalEmail();
        }
      },
      [showEmailDropdown, handleAddExternalEmail],
    );

    // Remove external email
    const handleRemoveExternalEmail = useCallback((email: string) => {
      setExternalEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    // Cancel
    const handleCancel = useCallback(() => {
      onClose();
    }, [onClose]);

    // Add - build CcMember[] and pass back
    const handleAdd = useCallback(() => {
      const members: CcMember[] = [];

      // Add selected customer members
      for (const id of selectedIds) {
        const member = allMembers.find((m) => m.mapping_id === id);
        if (member) {
          members.push({
            user_id: member.mapping_id,
            member_type: member.member_type,
            display_name: member.display_name,
            first_name: member.first_name,
            last_name: member.last_name,
            email: member.email,
          });
        }
      }

      // Add external emails
      for (const email of externalEmails) {
        members.push({
          user_id: email,
          member_type: "external",
          display_name: email,
          email,
        });
      }

      onAdd(members);
      onClose();
    }, [selectedIds, allMembers, externalEmails, onAdd, onClose]);

    // Show no results
    const showNoResults =
      searchQuery.length > 0 &&
      filteredMembers.length === 0 &&
      !showEmailDropdown;

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCancel}
        title="Select participants"
        drawerStyle={{ width: "404px" }}
        primaryButtonText="Add"
        onPrimaryButtonClick={handleAdd}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleCancel}
        headerStyle={{
          borderBottom: "none",
        }}
        additionalHeaderComponent={
          <Box
            sx={{
              padding: "12px 16px",
              borderTop: `1px solid ${theme.palette.neutral["015"]}`,
            }}
          >
            <SearchInput
              fullWidth
              name="participant-search"
              placeholder="Search participants or add an external mail ID"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              hideHelperBox
              inputProps={{ "aria-label": "Search participants" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.neutral["005"],
                  "& fieldset": {
                    borderColor: theme.palette.neutral["010"],
                  },
                },
              }}
            />

            {/* Email suggestion dropdown */}
            {showEmailDropdown && (
              <Paper
                elevation={2}
                sx={{
                  mt: "4px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={handleAddExternalEmail}
                  sx={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.neutral["005"],
                    },
                  }}
                >
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    Use this address : @{searchQuery}
                  </Typography>
                </Box>
                <Box sx={{ padding: "10px 16px" }}>
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.lightBg.low }}
                  >
                    We couldn&apos;t find the person you were looking for.
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* External email chips */}
            {externalEmails.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  mt: "8px",
                }}
              >
                {externalEmails.map((email) => (
                  <ChipV2
                    key={email}
                    variant="info"
                    clearable
                    onClear={() => handleRemoveExternalEmail(email)}
                  >
                    {email}
                  </ChipV2>
                ))}
              </Box>
            )}

            {/* External email warning banner */}
            {externalEmails.length > 0 && (
              <Stack
                direction="row"
                sx={{
                  mt: "8px",
                  backgroundColor: orange[50],
                  border: `1px solid ${orange[800]}`,
                  borderRadius: "4px",
                  px: "16px",
                  py: "8px",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <InfoIcon sx={{ fontSize: "20px" }} htmlColor={orange[800]} />
                <Typography variant="body-small" sx={{ color: orange[800] }}>
                  This is an external mail ID
                </Typography>
              </Stack>
            )}
          </Box>
        }
        bodySx={{ overflow: "auto" }}
      >
        <Box sx={{ padding: "8px 24px" }}>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low }}
          >
            Total members ({totalCount})
          </Typography>
        </Box>

        {filteredMembers.map((cm) => (
          <MemberListItem
            key={cm.mapping_id}
            member={{
              user_id: cm.mapping_id,
              display_name: getFullName(cm.first_name, "", cm.last_name),
              member_type: cm.member_type,
              member_source: "cc",
              email: cm.email,
            }}
            isSelected={selectedIds.has(cm.mapping_id)}
            onToggle={handleToggle}
            isLimitedAccess={cm.limited_access === true}
            isInactive={!cm.is_active}
            level="ticket"
          />
        ))}

        {showNoResults && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "24px",
            }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No results found
            </Typography>
          </Box>
        )}
      </Drawer>
    );
  },
);

SelectParticipantsDrawer.displayName = "SelectParticipantsDrawer";
